import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import {
  BodyMedium,
  Heading4Text,
  TitleSmall,
} from '../../../components/Texts';
import TextInput from '../../../components/Inputs/Text';
import { formatUSDEnsure2Digits } from '../../../common/currency';
import { useEffect } from 'react';
import Button from '../../../components/Button';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useTokenSearch } from '../../../hooks/useTokenSearch';
import { hapticsImpactLight } from '../../../common/haptic';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { useState } from 'react';

const TokenContainer = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  padding: 12px 10px;
  gap: 8px;
  display: flex;
  align-items: center;
  height: 52px;
  width: 100%;
  cursor: pointer;
`;

const TokenScroll = styled.div`
  ${({ $isMobile }) => ($isMobile ? 'height: calc(100vh - 270px);' : '')}
  width: 100%;
  overflow-y: scroll;
  max-height: ${({ $isMobile }) => ($isMobile ? 'unset' : '400px')};
`;

const SelectToken = ({
  onClose,
  title = 'Select an Asset to Gift',
  onContinue,
  tokens = null, // Optional tokens array to restrict selection
  filterToken = null, // Optional token to exclude from selection
  disableUsdc = false,
  avoidClosing = false,
}) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  const [search, setSearch] = useState('');

  const navigate = useNavigate();

  const {
    searchTerm,
    displayTokens,
    isLoading,
    isFetching,
    hasMore,
    setSearchTerm,
    loadMore,
  } = useTokenSearch(tokens, filterToken);

  const { ref, inView } = useInView({ threshold: 1 });

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isFetching) {
      loadMore();
    }
  }, [inView, hasMore, isFetching, loadMore]);

  const filteredTokens = displayTokens.filter(
    (token) => !(token.symbol === 'USDC' && disableUsdc),
  );

  return (
    <Modal
      onClose={onClose}
      customStyle={
        isMobile && {
          maxHeight: '95vh',
          height: '95vh',
          width: '100vw',
          borderRadius: '10px 10px 0px 0px',
        }
      }
      customOverlayStyle={isMobile ? { alignItems: 'flex-end' } : {}}
    >
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', gap: 15, flexDirection: 'column' }}>
          <Heading4Text
            style={{
              fontWeight: '600',
              marginTop: 20,
              color: theme.text.title,
            }}
          >
            {title}
          </Heading4Text>
          <TextInput
            placeholder='Search asset'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <TokenScroll $isMobile={isMobile}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {isLoading && filteredTokens.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                Loading tokens...
              </div>
            ) : filteredTokens.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                No tokens found matching your search.
              </div>
            ) : (
              filteredTokens.map((token, index) => {
                const isLast = index === filteredTokens.length - 1;
                return (
                  <TokenContainer
                    key={`${token.address}-${index}`}
                    ref={isLast ? ref : null}
                    onClick={() => {
                      hapticsImpactLight();
                      if (onContinue) {
                        if (!avoidClosing) {
                          onClose();
                        }
                        onContinue(token);
                      } else {
                        navigate(`/app/tokens/${token.address}`);
                        onClose();
                      }
                    }}
                  >
                    <img width={32} height={32} src={token.logoURI} />
                    <TitleSmall style={{ color: theme.text.title }}>
                      {token.name?.length > 15
                        ? `${token.name.slice(0, 15)}...`
                        : token.name}{' '}
                      <span
                        style={{
                          color: theme.text.secondary,
                          fontWeight: 300,
                          fontSize: 12,
                        }}
                      >
                        ({token.symbol})
                      </span>
                    </TitleSmall>
                    <BodyMedium
                      style={{ marginLeft: 'auto', color: theme.text.title }}
                    >
                      {formatUSDEnsure2Digits(token.usdPrice)}
                    </BodyMedium>
                  </TokenContainer>
                );
              })
            )}
            {isFetching && filteredTokens.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                Loading more tokens...
              </div>
            )}
          </div>
        </TokenScroll>
        <div style={{ marginLeft: 'auto' }}>
          <Button variant='white' onClick={onClose}>
            CANCEL
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SelectToken;
